import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Form from "../../../components/Form/Form";
import FormTitle from "../../../components/FormTitle/FormTitle";
import Input from "../../../components/Input/Input";
import Loader from "../../../components/Loader/Loader";
import { useAuth } from "../../../contexts/auth";
import TagService from "../../../services/TagService";
import "./FormTags.css";
import { LuTags } from "react-icons/lu";
import Select from "../../../components/Select/Select";
import { TagType } from "../../../enums/tagTypes";
import Button from "../../../components/Button/Button";
import { TagFields } from "../../../components/TagFields/TagFields";
import { TagFieldModal } from "../../../components/TagFieldModal/TagFieldModal";
import { TagTypeIcons } from "../../../utils/tagTypeIcons";
import TagList from "../../../components/TagList/TagList";
import ToolTip from "../../../components/Tooltip/Tooltip";
import { TagSelector } from "../../../components/TagSelector/TagSelector";
import ToggleButton from "../../../components/ToggleButton/ToggleButton";
import { IconSelector } from "../../../components/IconSelector/IconSelector";
import TextAreaInput from "../../../components/TextAreaInput/TextAreaInput";
import { LevelSelector } from "../../../components/LevelSelector/LevelSelector";

const ModalState = {
	CLOSE: 1,
	FIELD_SELECTOR: 2,
	FORM: 3,
}

function FormTags(props) {
	const { user } = useAuth();
	const [name, setName] = useState(props.tag?.name);
	const [color, setColor] = useState(props.tag?.color);
	const [icon, setIcon] = useState(props.tag?.icon);
	const [description, setDescription] = useState(props.tag?.description);
	const [type, setType] = useState(props.tag?.type ?? TagType.UNIT);
	const [fields, setFields] = useState([]);
	const [scopeTags, setScopeTags] = useState(props.tag?.scopeTags);
	const [showTagModal, setShowTagModal] = useState(false);
	const [selectedField, setSelectedField] = useState(null);
	const [metadata, setMetadata] = useState(null);
	const [areaControl, setAreaControl] = useState(false);
	const [parkingControl, setParkingControl] = useState(false);
	const [listInOrganization, setListInOrganization] = useState(props.tag?.listInOrganization || true);
	const [parkingLimit, setParkingLimit] = useState(props.tag?.parkingLimit || false);
	const [sendToDevices, setSendToDevices] = useState(props.tag?.sendToDevices || false);
	const [linkableToUser, setLinkableToUser] = useState(props.tag?.linkableToUser || false);
	const [licensePerson, setLicensePerson] = useState(props.tag?.licensePerson || false);
	const [modalMode, setModalMode] = useState(ModalState.CLOSE);
	const [showLoader, setShowLoader] = useState(false);
	const [levels, setLevels] = useState([]);

	useEffect(() => {
		setName(props.tag?.name);
		setColor(props.tag?.color);
		setDescription(props.tag?.description);
		setType(props.tag?.type ?? TagType.UNIT);
		setMetadata(props.tag?.metadata);
		setListInOrganization(props.tag?.listInOrganization === null || props.tag?.listInOrganization === undefined ? true : props.tag?.listInOrganization);
		setSendToDevices(props.tag?.sendToDevices || false);
		setFields(props.tag?.schema || []);
		setAreaControl(props.tag?.areaControl || false);
		setParkingControl(props.tag?.parkingControl || false);
		setParkingLimit(props.tag?.parkingLimit || false);
		setLinkableToUser(props.tag?.linkableToUser || false);
		setLicensePerson(props.tag?.licensePerson || false);
		setLevels(props.tag?.levels?.map(level => ({ unitId: `${level.unitId}`, hierarchy: level.childrenAuthorized })) || []);
		setScopeTags(props.tag?.scopeTags);
	}, [props.tag]);

	const navigate = useNavigate();

	const buildPayload = () => {
		let payload = {
			name,
			color,
			icon,
			description,
			type,
			fields,
			metadata,
			scopeTags: scopeTags.map(tag => tag.externalId),
			organizationId: user.userData.selectedOrganizationId,
			areaControl,
			parkingControl,
			parkingLimit,
			listInOrganization,
			sendToDevices,
			linkableToUser,
			licensePerson,
			levels: levels.map(level => ({
				unitId: isNaN(`${level.unitId}`) ? 'root' : `${level.unitId}`,
				childrenAuthorized: level.hierarchy
			})),
		};
		return payload;
	};

	async function handleSubmit(event) {
		if (props.type === "edicao") {
			handleEdit(event);
			return;
		}
		event.preventDefault();
		const payload = buildPayload();
		const response = await TagService.createTag(payload);
		if (!response) return;
		if (response.data.id) navigate("../consulta-tags", {
			state: { refresh: true }
		});
	}

	async function handleEdit(event) {
		event.preventDefault();
		const payload = buildPayload();
		const response = await TagService.updateTag(
			props.tag.id,
			payload
		);
		if (response.data.id) navigate("../consulta-tags");
	}

	async function handleDelete() {
		if (!props.tag) return;
		await TagService.deleteTag(props.tag.id);
		navigate("../consulta-tags", {
			state: { deletedId: props.tag.id },
		});
	}

	const handleClickEditButton = () => {
		if (!props.tag) return;
		navigate(`/tags/${props.tag.externalId}`, { state: props.tag });
	};

	function handleSaveField(field) {
		if (!field.name || field.name === '') {
			return;
		}
		const currentFieldIdx = fields.findIndex(f => f.externalId === field.externalId);
		if (currentFieldIdx !== -1) {
			setFields(prevFields => {
				const _fields = [...prevFields];
				_fields[currentFieldIdx] = {
					..._fields[currentFieldIdx],
					...field,
				}
				return _fields;
			})
			return;
		}
		setFields([...fields, field]);
	}

	function handleRemoveField(index) {
		const newList = [...fields];
		newList.splice(index, 1);
		setFields(newList);
	}

	function handleEditField(index) {
		setSelectedField(fields[index]);
		setModalMode(ModalState.FORM);
	}

	function handleRemoveTag(index) {
		const newList = [...scopeTags];
		newList.splice(index, 1);
		setScopeTags(newList);
	}

	const materiality = metadata?.__defaultValues__?.materiality ?? null;
	const parentAddress = metadata?.__defaultValues__?.parentAddress ?? null;

	return (
		<React.Fragment>
			<FormTitle
				type={props.type}
				tituloConsulta={props.tag?.name_local || null}
				tituloCadastro="Cadastro de Etiqueta"
				icon={<LuTags size={20} />}
				backRoute={"/consulta-tags"}
			/>
			{showLoader ? (
				<Loader />
			) : (
				<>
					<Form
						handleSubmit={handleSubmit}
						type={props.type}
						handleClickEdit={handleClickEditButton}
						handleDelete={handleDelete}
						navigateCancelUrl="../consulta-tags"
						askForPasswordOnDelete={true}
					>
						<p className="input-label">Tipo da etiqueta</p>
						<div className="tag-type-selector">
							{/* {user.userData?.hasRootAccess && (
								<div>
									<input
										id="organization"
										type="radio"
										name="tag-type"
										disabled={props.type !== "cadastro"}
										value={TagType.ORGANIZATION}
										onChange={e => setType(e.target.value)}
										checked={type === TagType.ORGANIZATION}
									/>
									<label htmlFor="organization">
										{React.createElement(TagTypeIcons.ORGANIZATION, { size: 27 })}
										Organização
									</label>
								</div>
							)} */}
							<div>
								<input
									id="person"
									type="radio"
									name="tag-type"
									disabled={props.type !== "cadastro"}
									value={TagType.PERSON}
									onChange={e => setType(e.target.value)}
									checked={type === TagType.PERSON}
								/>
								<label htmlFor="person" className="flex-row! items-center! w-fit! h-fit!">
									{React.createElement(TagTypeIcons.PERSON, { size: 27 })}
									Pessoa
								</label>
							</div>
							<div>
								<input
									id="unit"
									type="radio"
									name="tag-type"
									disabled={props.type !== "cadastro"}
									value={TagType.UNIT}
									onChange={e => setType(e.target.value)}
									checked={type === TagType.UNIT}
								/>
								<label htmlFor="unit" className="flex-row! items-center! w-fit! h-fit!">
									{React.createElement(TagTypeIcons.UNIT, { size: 27 })}
									Unidade
								</label>
							</div>
						</div>
						<div className={`${props.type !== "consulta" ? "grid-2" : "w-full"}`}>
							<Input
								name="name"
								id="name"
								labelText="Nome *"
								value={name}
								onChange={({ target }) => setName(target.value)}
								disabled={props.type === "consulta"}
								required={true}
								tooltipText="Nome da etiqueta"
								tooltipVisible={true}
							/>
							<div className="grid-2">
								<Input
									name="color"
									id="color"
									type="color"
									labelText="Cor"
									value={color}
									onChange={({ target }) => setColor(target.value)}
									disabled={props.type === "consulta"}
									required={false}
								/>
								<IconSelector
									name="icon"
									id="icon"
									type="icon"
									labelText="Ícone"
									value={icon}
									onChange={({ target }) => setIcon(target.value)}
									disabled={props.type === "consulta"}
									required={false}
								/>
							</div>
						</div>
						<TextAreaInput
							name={"description"}
							id="description"
							labelText={"Descrição"}
							onChange={({ target }) => setDescription(target.value)}
							value={description}
							hidden={props.type === "consulta"}
							rows={3}
						/>
						{props.type !== "consulta" && (
							<>
								<LevelSelector
									onChange={(levels) => {
										setLevels(levels.map(level => ({
											unitId: level.unitId,
											hierarchy: level.childrenAuthorized
										})));
									}}
									levels={levels.map(level => ({
										unitId: level.unitId,
										childrenAuthorized: level.hierarchy
									}))}
									showRoot={true}
									type={props.type}
									title={"Níveis"}
									description={"Selecione os níveis que a tag pertence"} />
							</>
						)}
						<div className="fields-container" style={{ width: '100%', marginBottom: 5, marginTop: 5 }}>
							<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
								<h4 className="input-label" style={{ marginBottom: 0 }}>Campos</h4>
								<div className="container-buttons" style={{ width: '100%', flexDirection: 'row', justifyContent: 'end', marginTop: 0 }}>
									{props.type !== "consulta" && (
										<Button
											label="Adicionar"
											action="add"
											onClick={() => {
												setSelectedField(null);
												setModalMode(ModalState.FIELD_SELECTOR);
											}}
											className="button-link"
										/>
									)}
								</div>
							</div>
							<TagFields
								fields={fields}
								showDetails={props.type !== "consulta"}
								onRemove={handleRemoveField}
								onEdit={handleEditField}
								disabled={props.type === "consulta"}
								showActions={props.type !== "consulta"}
							/>
						</div>
						<>
							{props.type !== "consulta" && (
								<>
									<div className="grid-2">
										{type === TagType.UNIT && (
											<>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Listar em organização`}
														selected={listInOrganization}
														onClick={() => setListInOrganization(prev => !prev)}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															`Listar ${type === TagType.PERSON ? 'os perfis' : 'as unidades'} com esta etiqueta em organização`
														}
													/>
												</div>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Enviar para dispositivo(s)`}
														selected={sendToDevices}
														onClick={() => setSendToDevices(prev => !prev)}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															`Enviar ${type === TagType.PERSON ? 'perfis' : 'unidades'} com esta etiqueta para o(s) dispositivo(s) quando vinculados`
														}
													/>
												</div>
											</>
										)}
										{type === TagType.PERSON && (
											<>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Usuário de licença`}
														selected={licensePerson}
														onClick={() => setLicensePerson(prev => !prev)}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															`Indica que esta etiqueta pode representar usuários de licença (perfil)`
														}
													/>
												</div>
											</>
										)}
										{type === TagType.UNIT && (
											<>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Vinculável a usuário`}
														selected={linkableToUser}
														onClick={() => {
															setLinkableToUser(prev => !prev)
														}}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															"Torna as unidades, com esta etiqueta, vinculáveis aos usuários, como a relação de usuário - apartamento (Morador)"
														}
													/>
												</div>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Limitar vagas`}
														selected={parkingLimit}
														onClick={() => {
															setParkingLimit(prev => !prev)
															if (parkingControl) {
																setParkingControl(false);
															}
														}}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															"Limitar vagas para as unidades com esta etiqueta"
														}
													/>
												</div>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Controle de vagas`}
														selected={parkingControl}
														onClick={() => {
															setParkingControl(prev => !prev);
															if (parkingLimit) {
																setParkingLimit(false);
															}
														}}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															"Liberar controle de vagas para as unidades com esta etiqueta"
														}
													/>
												</div>
												<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
													<ToggleButton
														label={`Controle de área`}
														selected={areaControl}
														onClick={() => setAreaControl(prev => !prev)}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															"Liberar controle de área para as unidades com esta etiqueta"
														}
													/>
												</div>
											</>
										)}
									</div>
									{type === TagType.UNIT && (
										<>
											<h3 className="bordered muted-md">Escopo</h3>

											<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center' }}>
												<ToggleButton
													label={`Utilizar etiquetas de escopo`}
													selected={metadata?.__useScopeTags__ || false}
													onClick={() => setMetadata(prevMetadata => ({
														...prevMetadata,
														__useScopeTags__: !(prevMetadata?.__useScopeTags__ || false)
													}))}
													disabled={props.type === "consulta"}
													tooltipVisible={true}
													tooltipText={
														"Limitar etiquetas nas unidades/pessoas abaixo na hierarquia"
													}
												/>
											</div>
										</>
									)}
								</>
							)}
							{type === TagType.UNIT && (
								<>
									{metadata?.__useScopeTags__ && (
										<div className="tags-container" style={{ width: '100%' }}>
											<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
												<h4 className="input-label" style={{ marginBottom: 0, display: 'flex', flexDirection: 'row' }}>
													Etiquetas de escopo
													<ToolTip
														tooltipVisible={true}
														tooltipText={`Limitar etiquetas nas unidades/pessoas abaixo na hierarquia`}
													/>
												</h4>
												<div className="container-buttons" style={{ width: '100%', flexDirection: 'row', justifyContent: 'end', marginTop: 0 }}>
													{props.type !== "consulta" && (
														<Button
															label="Adicionar"
															action="add"
															onClick={() => setShowTagModal(true)}
															className="button-link"
														/>
													)}
												</div>
											</div>
											<TagList
												tags={scopeTags}
												onRemove={handleRemoveTag}
												disabled={props.type === "consulta"}
											/>
										</div>
									)}
									{props.type !== "consulta" && (
										<>
											<h3 className="bordered muted-md">Valores padrões</h3>
											<div style={{ width: '100%', display: 'flex', justifyContent: 'start', alignItems: 'center', gap: '17px' }}>
												<ToggleButton
													label={`Utilizar valores padrões`}
													selected={metadata?.__useDefaultValues__ || false}
													onClick={() => setMetadata(prevMetadata => ({
														...prevMetadata,
														__useDefaultValues__: !(prevMetadata?.__useDefaultValues__ || false)
													}))}
													disabled={props.type === "consulta"}
													tooltipVisible={true}
													tooltipText={
														"Marque para utilizar/forçar valores padrões definidos abaixo"
													}
												/>
												{metadata?.__useDefaultValues__ && (
													<ToggleButton
														label={`Forçar valores padrões`}
														selected={metadata?.__forceDefaultValues__ || false}
														onClick={() => setMetadata(prevMetadata => ({
															...prevMetadata,
															__forceDefaultValues__: !(prevMetadata?.__forceDefaultValues__ || false)
														}))}
														disabled={props.type === "consulta"}
														tooltipVisible={true}
														tooltipText={
															"Marque para forçar valores padrões definidos abaixo"
														}
													/>
												)}
											</div>
											{metadata?.__useDefaultValues__ && (
												<>
													<div className={`${(materiality === 1 && props.type !== 'consulta') ? 'grid-2' : 'w-full'}`}>
														<Select
															labelText="Materialidade"
															name="materiality"
															id="materiality"
															value={materiality}
															disabled={props.type === "consulta"}
															onChange={({ target }) =>
																setMetadata(prevMeta => ({
																	...prevMeta,
																	__defaultValues__: {
																		...prevMeta.__defaultValues__,
																		materiality: Number(target.value),
																	}
																}))
															}
															required={true}
															options={[
																{ value: 1, text: "Unidade Física" },
																{ value: 2, text: "Unidade Lógica" },
															]}
														/>
														{materiality === 1 && (
															<div style={{ display: 'flex', justifyContent: 'start', alignItems: 'center', paddingTop: 20 }}>
																<ToggleButton
																	label={`Utilizar endereço do parente`}
																	selected={parentAddress}
																	onClick={() => setMetadata(prevMeta => ({
																		...prevMeta,
																		__defaultValues__: {
																			...prevMeta.__defaultValues__,
																			parentAddress: !parentAddress,
																		}
																	}))}
																	disabled={props.type === "consulta"}
																	tooltipVisible={true}
																	tooltipText={
																		"Essa unidade deve reutilizar o endereço da unidade pai."
																	}
																/>
															</div>
														)}
													</div>
												</>
											)}
										</>
									)}
								</>
							)}
						</>
					</Form>
					<TagFieldModal
						field={selectedField}
						modalMode={modalMode}
						setModalMode={setModalMode}
						tagType={type}
						onClose={() => setModalMode(ModalState.CLOSE)}
						onSave={handleSaveField}
					/>
					<TagSelector
						tags={scopeTags}
						isOpen={showTagModal}
						onClose={() => setShowTagModal(false)}
						onLoad={(loadedTags) => {
							setScopeTags(loadedTags.filter(lTag => props.tag?.scopeTags?.find(oTag => oTag.externalId === lTag.externalId)));
						}}
						onTagsChange={(tags) => {
							setScopeTags(tags)
						}} />
				</>
			)}
		</React.Fragment>
	);
}

export default FormTags;
